import React, { useState, useEffect } from 'react';
import ColorUtils from '../utils/colors'
import logo256 from '../assets/logo_256.png'
import localization from '../utils/localization'
import FormInput from '../components/FormInput'
import {
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  OutlinedInput,
  Dialog,
  DialogContent,
  Box,
  Typography,
  Stack,
  Paper,
  Fade,
  Grow,
  IconButton
} from '@mui/material';
import ServerUtils from '../utils/server'
import toast from 'react-hot-toast';
import AesUtils from '../utils/aes'
import MasterPassUtils from '../utils/masterpass'
import { useNavigate } from "react-router-dom";

function Login() {
  const navigate = useNavigate();
  const [agreementDialogOpen, setAgreementDialogOpen] = useState(true)
  const [mode, setMode] = useState('login')
  const [captcha, setCaptcha] = useState('')

  function refreshCaptcha() {
    ServerUtils.newCaptcha().then(async (resp) => {
      if (!resp.ok) {
        toast.error(localization.strings.networkErr)
        return
      }
      let respJson = await resp.json()
      if (respJson.errno !== 0) {
        toast.error(ServerUtils.handleError(respJson))
        return
      }
      setCaptcha(respJson.data.captcha_id)
    })
  }

  // login params
  const [loginAccount, setLoginAccount] = useState('')
  const [loginAccountErr, setLoginAccountErr] = useState('')
  const [loginMasterPassword, setLoginMasterPassword] = useState('')
  const [loginMasterPasswordErr, setLoginMasterPasswordErr] = useState('')

  function login() {
    let hasErr = false;
    if (loginAccount === '') {
      setLoginAccountErr(localization.strings.required)
      hasErr = true;
    }
    if (loginMasterPassword === '') {
      setLoginMasterPasswordErr(localization.strings.required)
      hasErr = true;
    }
    if (hasErr) return
    let currTime = parseInt(new Date().getTime() / 1000)
    let signedAccount = AesUtils.encryptData(loginAccount, loginMasterPassword, loginAccount)
    let text = loginAccount + '-' + signedAccount + '-' + currTime
    ServerUtils.login({
      account: loginAccount,
      time: currTime,
      token: AesUtils.sha256(text),
    }).then(async (resp) => {
      if (!resp.ok) {
        try {
          let respJson = await resp.json()
          if (respJson.errno !== 0) {
            toast.error(ServerUtils.handleError(respJson))
            return
          }
        } catch (e) { }
        toast.error(localization.strings.networkErr)
        return
      }
      let respJson = await resp.json()
      if (respJson.errno !== 0) {
        toast.error(ServerUtils.handleError(respJson))
        return
      }
      MasterPassUtils.setMasterPass(loginAccount, loginMasterPassword)
      navigate(`/main.html?lang=${localization.language.lang}`)
    })
  }

  // register params
  const [registerAccount, setRegisterAccount] = useState('')
  const [registerAccountErr, setRegisterAccountErr] = useState('')
  const [registerMasterPassword, setRegisterMasterPassword] = useState('')
  const [registerMasterPasswordErr, setRegisterMasterPasswordErr] = useState('')
  const [registerMasterRePassword, setRegisterMasterRePassword] = useState('')
  const [registerMasterRePasswordErr, setRegisterMasterRePasswordErr] = useState('')
  const [registerCaptcha, setRegisterCaptcha] = useState('')
  const [registerCaptchaErr, setRegisterCaptchaErr] = useState('')
  const [registerVisitCode, setRegisterVisitCode] = useState('')

  let isRegister = false;
  function register() {
    if (isRegister) return
    isRegister = true
    let hasErr = false;
    if (registerAccount === '') {
      setRegisterAccountErr(localization.strings.required)
      hasErr = true;
    }
    if (registerMasterPassword === '') {
      setRegisterMasterPasswordErr(localization.strings.required)
      hasErr = true;
    }
    if (registerMasterRePassword === '') {
      setRegisterMasterRePasswordErr(localization.strings.required)
      hasErr = true;
    } else if (registerMasterPassword !== registerMasterRePassword) {
      setRegisterMasterRePasswordErr(localization.strings.masterPasswordNotMatch)
      hasErr = true;
    }
    if (registerCaptcha === '') {
      setRegisterCaptchaErr(localization.strings.required)
      hasErr = true;
    }
    if (hasErr) {
      isRegister = false
      return
    }
    let signedAccount = AesUtils.encryptData(registerAccount, registerMasterPassword, registerAccount)
    ServerUtils.register({
      account: registerAccount,
      signed_account: signedAccount,
      be_invited_code: registerVisitCode,
      language: localization.language.lang,
      captcha_id: captcha,
      captcha_solution: registerCaptcha,
    }).then(async (resp) => {
      isRegister = false
      if (!resp.ok) {
        try {
          let respJson = await resp.json()
          if (respJson.errno !== 0) {
            if (respJson.errno === 4000030) refreshCaptcha()
            toast.error(ServerUtils.handleError(respJson))
            return
          }
        } catch (e) { }
        toast.error(localization.strings.networkErr)
        return
      }
      toast.success(localization.strings.successRegister)
      setRegisterAccount('')
      setRegisterMasterPassword('')
      setRegisterMasterRePassword('')
      setRegisterCaptcha('')
      setRegisterVisitCode('')
      setMode('login')
    })
  }

  useEffect(() => {
    ServerUtils.userInfo().then(async (resp) => {
      if (!resp.ok) return
      let respJson = await resp.json()
      if (respJson.errno !== 0) {
        if (respJson.errno === 4000030) refreshCaptcha()
        return
      }
      navigate(`/main.html?lang=${respJson.data.language}`)
    })
  }, [])

  return (
    <Box sx={{
      height: '100%',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#F8FAFC',
      backgroundImage: `
        radial-gradient(at 0% 0%, rgba(30, 136, 229, 0.05) 0px, transparent 50%),
        radial-gradient(at 100% 0%, rgba(139, 195, 74, 0.05) 0px, transparent 50%),
        radial-gradient(at 100% 100%, rgba(103, 58, 183, 0.05) 0px, transparent 50%),
        radial-gradient(at 0% 100%, rgba(244, 67, 54, 0.05) 0px, transparent 50%)
      `,
      p: 3,
      fontFamily: "'Inter', sans-serif",
    }}>
      <Grow in={true} timeout={800}>
        <Paper elevation={0} sx={{
          width: '100%',
          maxWidth: '440px',
          borderRadius: '32px',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.5)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
          position: 'relative',
        }}>
          {/* Language Switcher */}
          <Box sx={{ position: 'absolute', top: 24, right: 24 }}>
            <FormControl size="small">
              <Select
                value={localization.language.lang || "en"}
                onChange={(e) => window.location.href = '?lang=' + e.target.value}
                sx={{
                  backgroundColor: 'rgba(0,0,0,0.04)',
                  borderRadius: '10px',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  height: '2rem',
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                }}
              >
                <MenuItem value="en">EN</MenuItem>
                <MenuItem value="zh">中文</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ p: { xs: 4, sm: 6 } }}>
            <Stack alignItems="center" spacing={3}>
              <Box sx={{
                p: '2px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #1E88E5 0%, #1565C0 100%)',
                boxShadow: '0 8px 24px rgba(30, 136, 229, 0.2)',
                display: 'flex',
                mb: 1,
              }}>
                <Box
                  component="img"
                  src={logo256}
                  sx={{
                    height: '4rem',
                    width: '4rem',
                    borderRadius: '14px',
                    backgroundColor: '#fff',
                    p: '2px',
                  }}
                />
              </Box>

              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#1E293B', fontFamily: "'Poppins', sans-serif", mb: 1 }}>
                  {mode === 'login' ? localization.strings.login : localization.strings.register}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500 }}>
                  {mode === 'login' ? 'Securely access your encrypted vault' : 'Start your secure digital journey'}
                </Typography>
              </Box>

              {mode === 'login' ? (
                <Stack spacing={2.5} sx={{ width: '100%' }}>
                  <FormInput
                    start_icon="icon-person"
                    label={localization.strings.account}
                    placeholder={localization.strings.inputAccountHint}
                    error={loginAccountErr}
                    onChange={(e) => {
                      setLoginAccount(e.target.value)
                      setLoginAccountErr('')
                    }}
                  />
                  <FormInput
                    type="password"
                    start_icon="icon-lock"
                    label={localization.strings.masterPassword}
                    placeholder={localization.strings.inputMasterPasswordHint}
                    error={loginMasterPasswordErr}
                    onChange={(e) => {
                      setLoginMasterPassword(e.target.value)
                      setLoginMasterPasswordErr('')
                    }}
                    onKeyDown={(e) => e.keyCode === 13 && login()}
                  />

                  <Box sx={{
                    p: 2,
                    borderRadius: '16px',
                    backgroundColor: 'rgba(239, 68, 68, 0.05)',
                    border: '1px solid rgba(239, 68, 68, 0.1)'
                  }}>
                    <Typography variant="caption" sx={{ color: '#EF4444', fontWeight: 600, display: 'flex', alignItems: 'flex-start' }}>
                      <i className="iconfont icon-lock" style={{ marginRight: '8px', marginTop: '2px' }}></i>
                      {localization.strings.masterPasswordAlert}
                    </Typography>
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={login}
                    sx={{
                      borderRadius: '14px',
                      py: 1.8,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '1rem',
                      backgroundColor: '#1E88E5',
                      boxShadow: '0 8px 24px rgba(30, 136, 229, 0.25)',
                      '&:hover': { backgroundColor: '#1565C0', boxShadow: '0 12px 32px rgba(30, 136, 229, 0.35)' }
                    }}
                  >
                    {localization.strings.login}
                  </Button>

                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => { setMode('register'); refreshCaptcha(); }}
                    sx={{
                      borderRadius: '14px',
                      color: '#64748B',
                      textTransform: 'none',
                      fontWeight: 700,
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)', color: '#1E88E5' }
                    }}
                  >
                    {localization.strings.goRegister}
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={2.5} sx={{ width: '100%' }}>
                  <FormInput
                    start_icon="icon-person"
                    label={localization.strings.account}
                    placeholder={localization.strings.inputAccountHint}
                    error={registerAccountErr}
                    onChange={(e) => {
                      setRegisterAccount(e.target.value)
                      setRegisterAccountErr('')
                    }}
                  />
                  <FormInput
                    type="password"
                    start_icon="icon-lock"
                    label={localization.strings.masterPassword}
                    placeholder={localization.strings.inputMasterPasswordHint}
                    error={registerMasterPasswordErr}
                    onChange={(e) => {
                      setRegisterMasterPassword(e.target.value)
                      setRegisterMasterPasswordErr('')
                      setRegisterMasterRePasswordErr('')
                    }}
                  />
                  <FormInput
                    type="password"
                    start_icon="icon-lock"
                    label={localization.strings.masterPassword}
                    placeholder={localization.strings.inputMasterPasswordHintRepeat}
                    error={registerMasterRePasswordErr}
                    onChange={(e) => {
                      setRegisterMasterRePassword(e.target.value)
                      setRegisterMasterRePasswordErr('')
                    }}
                  />

                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      component="img"
                      src={`/openapi/captcha/${captcha}.png`}
                      onClick={refreshCaptcha}
                      sx={{
                        height: '48px',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        border: '1px solid rgba(0,0,0,0.08)',
                        transition: 'opacity 0.2s',
                        '&:hover': { opacity: 0.8 }
                      }}
                    />
                    <FormInput
                      start_icon="icon-image"
                      label={localization.strings.captcha}
                      placeholder={localization.strings.inputCaptchaHint}
                      error={registerCaptchaErr}
                      onChange={(e) => {
                        setRegisterCaptcha(e.target.value)
                        setRegisterCaptchaErr('')
                      }}
                      onKeyDown={(e) => e.keyCode === 13 && register()}
                    />
                  </Stack>

                  <Box sx={{
                    p: 2,
                    borderRadius: '16px',
                    backgroundColor: 'rgba(239, 68, 68, 0.05)',
                    border: '1px solid rgba(239, 68, 68, 0.1)'
                  }}>
                    <Typography variant="caption" sx={{ color: '#EF4444', fontWeight: 600, display: 'flex', alignItems: 'flex-start' }}>
                      <i className="iconfont icon-lock" style={{ marginRight: '8px', marginTop: '2px' }}></i>
                      {localization.strings.masterPasswordAlert}
                    </Typography>
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={register}
                    sx={{
                      borderRadius: '14px',
                      py: 1.8,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '1rem',
                      backgroundColor: '#1E88E5',
                      boxShadow: '0 8px 24px rgba(30, 136, 229, 0.25)',
                      '&:hover': { backgroundColor: '#1565C0', boxShadow: '0 12px 32px rgba(30, 136, 229, 0.35)' }
                    }}
                  >
                    {localization.strings.register}
                  </Button>

                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => setMode('login')}
                    sx={{
                      borderRadius: '14px',
                      color: '#64748B',
                      textTransform: 'none',
                      fontWeight: 700,
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)', color: '#1E88E5' }
                    }}
                  >
                    {localization.strings.goLogin}
                  </Button>
                </Stack>
              )}
            </Stack>
          </Box>
        </Paper>
      </Grow>

      {/* Agreement Dialog */}
      <Dialog
        open={agreementDialogOpen}
        onClose={() => { }}
        PaperProps={{
          sx: { borderRadius: '24px', p: 1, maxWidth: '400px' }
        }}
      >
        <DialogContent>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, fontFamily: "'Poppins', sans-serif" }}>
            {localization.strings.easypassPromise}
          </Typography>
          <Stack spacing={1.5} sx={{ mb: 3 }}>
            {[
              localization.strings.promiseItem1,
              localization.strings.promiseItem2,
              localization.strings.promiseItem3,
              localization.strings.promiseItem4
            ].map((text, i) => (
              <Typography key={i} variant="body2" sx={{ color: '#64748B', lineHeight: 1.6 }}>{text}</Typography>
            ))}
          </Stack>

          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: '#1E293B' }}>
            {localization.strings.userNotice}
          </Typography>
          <Stack spacing={1} sx={{ mb: 4 }}>
            <Typography variant="body2" sx={{ color: '#64748B' }}>{localization.strings.userNoticeItem1}</Typography>
            <Typography variant="body2" sx={{ color: '#64748B' }}>{localization.strings.userNoticeItem2}</Typography>
          </Stack>

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => setAgreementDialogOpen(false)}
            sx={{
              borderRadius: '12px',
              py: 1.5,
              textTransform: 'none',
              fontWeight: 700,
              backgroundColor: '#1E88E5',
            }}
          >
            {localization.strings.agree}
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default Login;